import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { colors as C, fonts as F } from '../theme'
import { useIsMobile } from '../hooks/useIsMobile'

const h2Style = {
  fontSize: '18px',
  fontWeight: 500,
  color: C.textPrimary,
  marginBottom: '1rem',
}

const h3Style = {
  fontSize: '15px',
  fontWeight: 600,
  color: C.textPrimary,
  marginTop: '1.2rem',
  marginBottom: '0.6rem',
}

const pGap = { marginTop: '0.8rem' }
const sectionStyle = { marginBottom: '2.5rem' }

const InternalLink = ({ to, children }) => (
  <Link to={to} style={{ color: C.accent, textDecoration: 'underline' }}>{children}</Link>
)

export default function CgvPage() {
  const isMobile = useIsMobile()

  const cellStyle = {
    padding: '10px 12px',
    borderBottom: `0.5px solid ${C.border}`,
    fontSize: '0.82rem',
    lineHeight: 1.5,
    verticalAlign: 'top',
  }
  const thStyle = { ...cellStyle, fontWeight: 600, color: C.textPrimary, background: C.bgPage }

  return (
    <div style={{ background: C.bgPage, minHeight: '100vh' }}>
      {/* Header */}
      <header style={{
        background: C.bgCard,
        borderBottom: `0.5px solid ${C.border}`,
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: C.textPrimary,
            textDecoration: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
          }}>
            <ChevronLeft size={20} />
            Retour
          </Link>
          <div style={{
            fontSize: '1.2rem',
            fontFamily: F.serif,
            fontWeight: 400,
            color: C.textPrimary,
          }}>
            IAvocat<span style={{ fontStyle: 'italic', color: C.accent }}>.ai</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div style={{
        maxWidth: '760px',
        margin: '0 auto',
        padding: isMobile ? '32px 18px' : '60px 24px',
        background: C.bgCard,
        minHeight: '100vh',
      }}>
        <h1 style={{
          fontFamily: F.serif,
          fontSize: isMobile ? '28px' : '36px',
          fontWeight: 400,
          color: C.textPrimary,
          marginBottom: '0.5rem',
        }}>
          Conditions Générales de Vente
        </h1>
        <p style={{
          fontFamily: F.sans,
          fontSize: '12px',
          fontStyle: 'italic',
          color: C.textSecondary,
          marginBottom: '2rem',
        }}>
          Dernière mise à jour : 1er juin 2026 — Version 1.0
        </p>

        <div style={{ fontFamily: F.sans, lineHeight: 1.8, color: C.textSecondary }}>

          {/* Préambule */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>Préambule</h2>
            <p>
              Les présentes Conditions Générales de Vente (« CGV ») régissent les relations contractuelles entre l'éditeur de la plateforme IAvocat.ai et tout professionnel souscrivant un abonnement payant aux services de la Plateforme (« le Client » ou « l'Abonné »).
            </p>
            <p style={pGap}>
              La Plateforme est exploitée par <strong>MY LEGAL ACT INFORMATION TECHNOLOGY CONSULTANTS - FZCO</strong> (ci-après « My Legal Act » ou « l'Éditeur »), Free Zone Company de droit émirien, immatriculée sous licence n° 66207 délivrée le 11 juillet 2025 par la Dubai Integrated Economic Zones Authority (DIEZ) au sein de l'International Free Zone Authority (IFZA), dont le siège social est situé IFZA Properties, DSO-IFZA, Dubai Silicon Oasis, Dubai, Émirats Arabes Unis. Coordonnées complètes et représentant dans l'Union européenne : voir les <InternalLink to="/mentions-legales">Mentions légales</InternalLink>.
            </p>
            <p style={pGap}>
              Les présentes CGV complètent les <InternalLink to="/cgu">Conditions Générales d'Utilisation</InternalLink> (« CGU ») et la <InternalLink to="/confidentialite">Politique de Confidentialité</InternalLink>, qui en font partie intégrante. En cas de contradiction sur un point relatif à la commercialisation et au paiement de l'abonnement, les présentes CGV prévalent.
            </p>
            <p style={pGap}>
              <strong>Souscription strictement professionnelle.</strong> Les services d'IAvocat.ai sont destinés exclusivement à des professionnels du droit (avocats inscrits à un barreau, cabinets d'avocats et leurs collaborateurs) agissant pour les besoins de leur activité professionnelle. La souscription s'effectue à titre professionnel. Le Client reconnaît ne pas agir en qualité de consommateur au sens de l'article liminaire du Code de la consommation.
            </p>
          </section>

          {/* 1. Objet */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>1. Objet</h2>
            <p>
              Les présentes CGV ont pour objet de définir les conditions dans lesquelles l'Éditeur fournit au Client un accès, sous forme d'abonnement, à la plateforme logicielle en ligne (SaaS) IAvocat.ai, suite intégrée d'assistance juridique par intelligence artificielle destinée aux avocats.
            </p>
            <p style={pGap}>
              La description fonctionnelle du service (chat IA juridique, gestion de dossiers, agenda, emails centralisés, génération de documents, recherche juridique) figure aux CGU. L'Éditeur se réserve le droit de faire évoluer les fonctionnalités, sans réduction substantielle du périmètre souscrit sans information préalable du Client.
            </p>
          </section>

          {/* 2. Formules d'abonnement */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>2. Formules d'abonnement et prix</h2>
            <p>
              La Plateforme est commercialisée sous forme d'abonnement mensuel, selon trois formules. Les prix sont exprimés en euros, <strong>hors taxes (HT)</strong>, par mois :
            </p>
            <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: `0.5px solid ${C.border}`, fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Formule</th>
                    <th style={thStyle}>Volume mensuel inclus</th>
                    <th style={thStyle}>Utilisateurs</th>
                    <th style={thStyle}>Prix HT / mois</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={cellStyle}><strong>Essentiel</strong></td>
                    <td style={cellStyle}>500 messages et 20 actes ou analyses</td>
                    <td style={cellStyle}>1</td>
                    <td style={cellStyle}>249 €</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>Cabinet</strong></td>
                    <td style={cellStyle}>1 500 messages et 60 actes ou analyses</td>
                    <td style={cellStyle}>1</td>
                    <td style={cellStyle}>549 €</td>
                  </tr>
                  <tr>
                    <td style={cellStyle}><strong>Cabinet+</strong></td>
                    <td style={cellStyle}>5 000 messages et 200 actes ou analyses</td>
                    <td style={cellStyle}>1</td>
                    <td style={cellStyle}>1 190 €</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={pGap}>
              Un « message » désigne toute requête conversationnelle adressée aux agents IA ; un « acte ou analyse » désigne toute production lourde (génération d'acte juridique, analyse de dossier). Les volumes s'entendent par mois calendaire et se réinitialisent le premier jour de chaque mois ; au-delà du volume inclus, l'accès aux agents IA est suspendu jusqu'à la réinitialisation ou au passage à une formule supérieure. Le détail du périmètre fonctionnel et la consommation en cours sont accessibles dans l'espace abonnement du Client. Chaque formule donne accès à un (1) utilisateur ; un mode multi-utilisateurs (postes supplémentaires par cabinet) sera proposé ultérieurement et fera l'objet d'une mise à jour de la présente grille. Les formules souscrites antérieurement à la présente grille demeurent régies par leurs conditions de souscription jusqu'à leur prochain renouvellement.
            </p>
            <p style={pGap}>
              <strong>TVA.</strong> Les prix sont indiqués hors taxes. La TVA est, le cas échéant, autoliquidée par le preneur assujetti établi dans un État membre de l'Union européenne, en application des articles 44 et 196 de la Directive 2006/112/CE (transposés aux articles 259 B et 283-2 du Code général des impôts français). Les preneurs non assujettis ou établis hors de l'Union européenne sont invités à contacter l'Éditeur pour les modalités de facturation applicables.
            </p>
            <p style={pGap}>
              <strong>Révision des prix.</strong> L'Éditeur peut modifier ses tarifs. Toute modification du prix de l'abonnement en cours est notifiée au Client au moins trente (30) jours avant son entrée en vigueur, par email. Le nouveau prix s'applique au renouvellement suivant. Le Client qui refuse la nouvelle tarification peut résilier son abonnement avant son entrée en vigueur, dans les conditions de l'article 8.
            </p>
          </section>

          {/* 3. Période d'essai */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>3. Période d'essai gratuite</h2>
            <p>
              Chaque nouveau Client bénéficie d'une période d'essai gratuite de quatorze (14) jours à compter de son inscription, limitée à cinquante (50) messages et trois (3) actes ou analyses. Aucun montant n'est prélevé pendant la période d'essai.
            </p>
            <p style={pGap}>
              Le Client peut résilier à tout moment pendant la période d'essai via les paramètres de son compte ; la résiliation prend alors effet immédiatement et aucun montant n'est prélevé. À l'issue de la période d'essai, l'accès aux agents IA est suspendu jusqu'à la souscription d'une formule payante ; les données du Client restent accessibles et exportables.
            </p>
          </section>

          {/* 4. Commande, formation et durée du contrat */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>4. Commande, formation et durée du contrat</h2>
            <p>
              La souscription s'effectue en ligne. Le contrat est formé lors de la validation de la commande par le Client (acceptation des présentes CGV et enregistrement du moyen de paiement). L'acceptation des CGV est matérialisée par une case à cocher préalable à la souscription.
            </p>
            <p style={pGap}>
              <strong>Durée et reconduction.</strong> L'abonnement est souscrit pour une durée d'un (1) mois, reconduit tacitement par périodes successives d'un (1) mois, à défaut de résiliation dans les conditions de l'article 8. Chaque période débute à la date d'activation de l'abonnement (ou à la date anniversaire mensuelle correspondante).
            </p>
          </section>

          {/* 5. Paiement (Stripe) */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>5. Modalités de paiement</h2>
            <p>
              Le paiement de l'abonnement s'effectue par carte bancaire, via le prestataire de paiement <strong>Stripe Payments Europe Ltd.</strong> (Irlande, UE). Les coordonnées bancaires du Client ne transitent jamais par les serveurs de l'Éditeur et sont traitées directement par Stripe selon les normes de sécurité applicables (PCI-DSS).
            </p>
            <p style={pGap}>
              Le premier prélèvement intervient à l'issue de la période d'essai de quatorze (14) jours, sauf résiliation préalable. Les prélèvements suivants sont effectués mensuellement, à terme à échoir, à la date anniversaire de l'abonnement. La facture correspondante est mise à disposition du Client dans son espace abonnement.
            </p>
            <p style={pGap}>
              <strong>Défaut de paiement.</strong> En cas d'échec de prélèvement, l'Éditeur peut suspendre l'accès au service après une relance restée infructueuse, jusqu'à régularisation. Conformément à la réglementation applicable aux transactions entre professionnels, tout retard de paiement entraîne de plein droit des pénalités de retard ainsi que l'indemnité forfaitaire pour frais de recouvrement prévue par le droit applicable.
            </p>
          </section>

          {/* 6. Droit de rétractation */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>6. Absence de droit de rétractation (souscription professionnelle)</h2>
            <p>
              Le Client souscrit l'abonnement dans le cadre de son activité professionnelle. Le service objet du contrat entre dans le champ de l'activité principale du Client. En conséquence, le droit de rétractation prévu par le Code de la consommation au bénéfice des consommateurs et de certains professionnels (article L. 221-3 du Code de la consommation) ne trouve pas à s'appliquer.
            </p>
            <p style={pGap}>
              La période d'essai gratuite de quatorze (14) jours (article 3) permet néanmoins au Client d'évaluer le service avant tout prélèvement et de résilier sans frais avant la fin de cette période.
            </p>
          </section>

          {/* 7. Disponibilité et SLA */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>7. Disponibilité du service et maintenance</h2>
            <p>
              L'Éditeur met en œuvre les moyens raisonnables pour assurer l'accessibilité de la Plateforme 24h/24 et 7j/7. Le service est fourni dans le cadre d'une obligation de moyens.
            </p>
            <p style={pGap}>
              <strong>Niveau de service indicatif.</strong> L'Éditeur vise un taux de disponibilité mensuel de 99 %, hors interruptions planifiées (maintenance) et hors causes échappant à son contrôle (incidents des fournisseurs d'hébergement et d'infrastructure, force majeure). Les opérations de maintenance programmée sont, dans la mesure du possible, réalisées en dehors des heures ouvrées et annoncées au préalable.
            </p>
            <p style={pGap}>
              Le service repose sur des infrastructures tierces (hébergement, base de données, modèles d'intelligence artificielle, paiement) décrites dans la <InternalLink to="/confidentialite">Politique de Confidentialité</InternalLink>. L'indisponibilité imputable à l'un de ces fournisseurs ne saurait engager la responsabilité de l'Éditeur au-delà des limites de l'article 9.
            </p>
          </section>

          {/* 8. Résiliation */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>8. Résiliation</h2>
            <h3 style={h3Style}>8.1 Résiliation par le Client</h3>
            <p>
              Le Client peut résilier son abonnement à tout moment, sans préavis ni frais, via les paramètres de son compte. La résiliation met fin à la reconduction tacite : l'accès au service est maintenu jusqu'au terme de la période mensuelle en cours déjà réglée, sauf demande expresse de cessation immédiate. Aucun remboursement n'est dû au titre d'une période mensuelle entamée.
            </p>
            <h3 style={h3Style}>8.2 Résiliation par l'Éditeur</h3>
            <p>
              L'Éditeur peut suspendre ou résilier l'accès, après mise en demeure restée sans effet pendant quinze (15) jours (sauf urgence ou manquement grave), en cas de violation des CGU ou des présentes CGV, de défaut de paiement, ou d'usage de la Plateforme contraire à la loi ou à l'ordre public.
            </p>
            <h3 style={h3Style}>8.3 Effets de la résiliation et restitution des données</h3>
            <p>
              À l'issue du contrat, les données du Client sont conservées puis supprimées dans les conditions et délais prévus par la <InternalLink to="/confidentialite">Politique de Confidentialité</InternalLink> (suppression sous 30 jours, suppression immédiate possible sur demande). Le Client est invité à exporter ses données avant la fin de l'abonnement. Une demande de portabilité peut être adressée à privacy@iavocat.ai.
            </p>
          </section>

          {/* 9. Responsabilité */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>9. Responsabilité et garanties</h2>
            <p>
              <strong>9.1 Rôle d'assistance de l'IA.</strong> La Plateforme fournit une assistance à l'avocat ; elle ne se substitue en aucun cas à son jugement professionnel. Le Client demeure seul responsable des actes juridiques, conseils et documents produits avec l'aide de la Plateforme, qu'il doit vérifier avant tout usage. L'Éditeur ne garantit ni l'exactitude, ni la complétude, ni l'adéquation des résultats générés par l'IA à une situation particulière.
            </p>
            <p style={pGap}>
              <strong>9.2 Limitation de responsabilité.</strong> La responsabilité de l'Éditeur, tous préjudices confondus et toutes causes confondues, est limitée au montant des sommes effectivement versées par le Client au titre de l'abonnement au cours des douze (12) mois précédant le fait générateur. L'Éditeur n'est pas responsable des préjudices indirects, immatériels ou consécutifs (perte d'exploitation, perte de chance, perte de données autres que celles imputables à un manquement prouvé de l'Éditeur, atteinte à l'image).
            </p>
            <p style={pGap}>
              <strong>9.3 Exclusions.</strong> Les présentes limitations ne s'appliquent pas en cas de dol, de faute lourde, ni dans les cas où la loi impérative applicable interdit une telle limitation (notamment en matière de dommages corporels).
            </p>
            <p style={pGap}>
              <strong>9.4 Force majeure.</strong> Aucune des parties ne saurait être tenue responsable d'un manquement résultant d'un cas de force majeure au sens du droit applicable.
            </p>
          </section>

          {/* 10. Données personnelles et sous-traitance */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>10. Données personnelles et sous-traitance (RGPD)</h2>
            <p>
              Dans le cadre de l'exécution du contrat, l'Éditeur traite des données personnelles pour le compte du Client. Les conditions de ce traitement — localisation des données dans l'Union européenne, sous-traitants ultérieurs (art. 28 RGPD), durées de conservation, sécurité et droits des personnes — sont détaillées dans la <InternalLink to="/confidentialite">Politique de Confidentialité</InternalLink>, qui vaut accord de traitement des données (DPA) entre les parties.
            </p>
            <p style={pGap}>
              <strong>Secret professionnel.</strong> Les données déposées par l'avocat sont susceptibles d'être couvertes par le secret professionnel (article 66-5 de la loi n° 71-1130 du 31 décembre 1971). L'architecture de la Plateforme (cloisonnement par avocat via Row-Level Security, chiffrement en transit et au repos, hébergement et traitement IA dans l'UE, non-utilisation des données pour l'entraînement des modèles) est conçue pour en préserver la confidentialité.
            </p>
            <p style={pGap}>
              <strong>Traitement exceptionnel hors UE.</strong> Un seul service (skill « specialiste-nullites ») peut, sur action expresse de l'avocat et après consentement éclairé horodaté, donner lieu à un traitement aux États-Unis (article 49.1.a RGPD), avec possibilité d'opt-out définitif. Les modalités complètes figurent à la Politique de Confidentialité.
            </p>
          </section>

          {/* 11. Propriété intellectuelle */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>11. Propriété intellectuelle</h2>
            <p>
              L'Éditeur conserve l'ensemble des droits de propriété intellectuelle sur la Plateforme, son code, ses interfaces et ses contenus. Le Client bénéficie d'un droit d'usage personnel, non exclusif et non transférable, pour la durée de son abonnement.
            </p>
            <p style={pGap}>
              Le Client conserve la propriété de ses données et des dossiers déposés. Les documents produits par la Plateforme à la demande du Client lui appartiennent, sous réserve du complet paiement de l'abonnement correspondant.
            </p>
          </section>

          {/* 12. Droit applicable et litiges */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>12. Droit applicable, règlement des litiges et médiation</h2>
            <p>
              Les présentes CGV sont régies par le <strong>droit français</strong>.
            </p>
            <p style={pGap}>
              <strong>12.1 Règlement amiable.</strong> Avant toute action contentieuse, les parties s'efforcent de résoudre amiablement tout différend. La partie la plus diligente adresse à l'autre une réclamation écrite motivée par email à contact@iavocat.ai. Les parties disposent de trente (30) jours à compter de la réception de cette réclamation pour parvenir à un accord.
            </p>
            <p style={pGap}>
              <strong>12.2 Médiation de la consommation.</strong> Le contrat étant conclu entre professionnels, le dispositif de médiation de la consommation (articles L. 611-1 et suivants du Code de la consommation), réservé aux litiges entre un consommateur et un professionnel, n'a pas vocation à s'appliquer. Les parties peuvent néanmoins, d'un commun accord, recourir à une médiation conventionnelle.
            </p>
            <p style={pGap}>
              <strong>12.3 Juridiction compétente.</strong> À défaut d'accord amiable, le litige est porté devant la juridiction compétente déterminée selon les règles applicables, notamment le Règlement (UE) 1215/2012 du 12 décembre 2012 (« Bruxelles I bis ») pour les Clients établis dans l'Union européenne.
            </p>
          </section>

          {/* 13. Modifications */}
          <section style={sectionStyle}>
            <h2 style={h2Style}>13. Modification des CGV</h2>
            <p>
              L'Éditeur peut modifier les présentes CGV. Toute modification substantielle est notifiée au Client au moins quinze (15) jours avant son entrée en vigueur. La poursuite de l'utilisation du service au-delà de cette date vaut acceptation des CGV modifiées. Le Client qui refuse les modifications peut résilier dans les conditions de l'article 8.
            </p>
          </section>

          <hr style={{ margin: '2.5rem 0', border: 'none', borderTop: `0.5px solid ${C.border}` }} />

          <p style={{ fontSize: '0.9rem', color: C.textSecondary, marginTop: '2rem' }}>
            Pour toute question relative à ces CGV ou à votre abonnement, contactez-nous à contact@iavocat.ai.
          </p>
        </div>
      </div>
    </div>
  )
}
